import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Header } from './components/Header';
import { LocationSelector } from './components/LocationSelector';
import { CategoryFilter } from './components/CategoryFilter';
import { SmartMomentBar, SmartFilterMode } from './components/SmartMomentBar';
import { SpotCard } from './components/SpotCard';
import { SpotDetailModal } from './components/SpotDetailModal';
import { MapView } from './components/MapView';
import { FavoritesDrawer } from './components/FavoritesDrawer';
import { RoutePlannerModal } from './components/RoutePlannerModal';
import { CurrencyConverterModal } from './components/CurrencyConverterModal';
import { WeekendPlannerModal } from './components/WeekendPlannerModal';
import { VeggieSnacksModal } from './components/VeggieSnacksModal';
import {
  Spot,
  SpotCategory,
  Region,
  UserLocation,
  Language,
  ViewMode,
  SortOption,
} from './types/spot';
import { SPOTS_DATA } from './data/spotsData';
import {
  augmentAndSortSpots,
  calculateDistance,
  detectRegionFromCoordinates,
  getGeolocationFailure,
  GPS_OPTIONS,
  isCoordinateInSouthKorea,
  MIN_ACCEPTABLE_GPS_ACCURACY_METERS,
} from './services/geolocation';
import {
  getCurrentDayMoment,
  isSpotPerfectNow,
  isIndoorSpot,
  findNearbyClusterSpots,
  DayMoment,
} from './services/momentEngine';
import { fetchLiveNearbyPois } from './services/overpassApi';
import { fetchKakaoLivePlaces } from './services/kakaoPlacesSearch';
import { loadKakaoMaps } from './services/kakaoMaps';
import { reverseGeocodeCoords } from './services/reverseGeocode';
import { PrivacyFooter } from './components/PrivacyFooter';
import confetti from 'canvas-confetti';
import {
  Layers,
  MapPin,
  Bookmark,
  Sparkles,
  Flame,
  SearchX,
  CheckCircle2,
  CalendarDays,
  Store,
} from 'lucide-react';

export const DEFAULT_SEOUL_LOCATION: UserLocation = {
  lat: 37.5759,
  lng: 126.9768,
  isGps: false,
  cityName: 'Seoul (Jongno)',
  streetAddressKr: '서울특별시 종로구 사직로 161 (세종로)',
  streetAddressEn: '161 Sajik-ro, Jongno-gu, Seoul',
};

function matchesSpotCategory(
  s: Spot,
  category: SpotCategory,
  region: Region,
  hasSearchQuery: boolean
): boolean {
  if (category === 'extras') {
    return Boolean(s.isExtra);
  }
  if (category === 'all') {
    // Text search query can match any spot directly
    if (hasSearchQuery) return true;
    // Real tourist attractions only: exclude dining niches, charging spots, and fitness/gyms
    if (region === 'Jeju') return s.category !== 'fitness';
    return (
      s.category !== 'vegetarian_food' &&
      s.category !== 'halal_food' &&
      !s.isVegetarianOnly &&
      s.category !== 'charging_spot' &&
      s.category !== 'fitness'
    );
  }
  if (category === 'charging_spot') {
    return Boolean(
      s.category === 'charging_spot' ||
        s.hasFreeCharging ||
        s.tags.some((t) => {
          const l = t.toLowerCase();
          return l.includes('charging') || l.includes('laden') || l.includes('충전');
        })
    );
  }
  if (category === 'activity') return s.category === 'activity';
  if (category === 'event_festival') return s.category === 'event_festival';
  if (category === 'vegetarian_food') {
    return Boolean(
      s.category === 'vegetarian_food' ||
        s.isVegetarianOnly ||
        s.dietType === 'vegan' ||
        s.dietType === 'vegetarian' ||
        s.dietType === 'universal' ||
        s.tags.some(
          (t) =>
            t.toLowerCase().includes('vegetarian') ||
            t.toLowerCase().includes('veggie') ||
            t.toLowerCase().includes('vegan')
        )
    );
  }
  if (category === 'halal_food') {
    return Boolean(
      s.category === 'halal_food' ||
        s.dietType === 'halal' ||
        s.dietType === 'halal_friendly' ||
        s.dietType === 'universal' ||
        s.isHalal ||
        s.tags.some((t) => t.toLowerCase().includes('halal'))
    );
  }
  if (category === 'shopping') return s.category === 'shopping';
  return s.category === category;
}

export const App: React.FC = () => {
  // ─── State ───
  const [language, setLanguage] = useState<Language>('de');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCategory, setSelectedCategory] = useState<SpotCategory>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [radiusKm, setRadiusKm] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('k_spotfinder_radius');
      if (saved) {
        const val = Number(saved);
        if (!isNaN(val) && val >= 0) return val;
      }
    } catch {}
    return 100;
  });
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [visitedFilter, setVisitedFilter] = useState<'all' | 'unvisited' | 'visited'>('all');

  // Smart Moment & Walking state
  const [smartFilter, setSmartFilter] = useState<SmartFilterMode>('all');
  const [customHour, setCustomHour] = useState<number | null>(null);
  const currentMoment: DayMoment = useMemo(() => getCurrentDayMoment(customHour ?? undefined), [customHour]);

  // Modal states
  const [selectedSpotDetail, setSelectedSpotDetail] = useState<Spot | null>(null);
  const [isCurrencyConverterOpen, setIsCurrencyConverterOpen] = useState<boolean>(false);
  const [isWeekendPlannerOpen, setIsWeekendPlannerOpen] = useState<boolean>(false);
  const [isVeggieSnacksOpen, setIsVeggieSnacksOpen] = useState<boolean>(false);

  // Initial user location: restore from localStorage only if saved and valid inside South Korea
  const [userLocation, setUserLocation] = useState<UserLocation>(() => {
    try {
      const saved = localStorage.getItem('k_spotfinder_user_loc');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          parsed &&
          typeof parsed.lat === 'number' &&
          typeof parsed.lng === 'number' &&
          isCoordinateInSouthKorea(parsed.lat, parsed.lng)
        ) {
          return parsed;
        }
      }
    } catch {}
    return DEFAULT_SEOUL_LOCATION;
  });

  const [selectedRegion, setSelectedRegion] = useState<Region>(() => {
    try {
      const savedLoc = localStorage.getItem('k_spotfinder_user_loc');
      if (savedLoc) {
        const parsed = JSON.parse(savedLoc);
        if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
          const detected = detectRegionFromCoordinates(parsed.lat, parsed.lng);
          if (detected) return detected;
        }
      }
    } catch {}
    try {
      const saved = localStorage.getItem('k_spotfinder_region');
      if (saved) return saved as Region;
    } catch {}
    return 'Seoul';
  });

  const handleSelectCategory = (cat: SpotCategory) => {
    setSelectedCategory(cat);
    if (cat === 'extras' && selectedRegion !== 'All') {
      setSelectedRegion('All');
    }
  };

  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [isLocationReady, setIsLocationReady] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationNotice, setLocationNotice] = useState<string | null>(null);
  const [isScanningLive, setIsScanningLive] = useState<boolean>(false);
  const [extraPois, setExtraPois] = useState<Spot[]>([]);
  const watchIdRef = useRef<number | null>(null);
  const lastReverseLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const reverseRequestIdRef = useRef(0);

  // Bookmarks
  const [savedSpotIds, setSavedSpotIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('k_spotfinder_saved');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Visited / Checked off spots
  const [visitedSpotIds, setVisitedSpotIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('k_spotfinder_visited');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem('k_spotfinder_user_loc', JSON.stringify(userLocation));
    } catch {
      // ignore
    }
  }, [userLocation]);

  useEffect(() => {
    try {
      localStorage.setItem('k_spotfinder_radius', radiusKm.toString());
    } catch {
      // ignore
    }
  }, [radiusKm]);

  useEffect(() => {
    try {
      localStorage.setItem('k_spotfinder_saved', JSON.stringify(savedSpotIds));
    } catch {
      // ignore
    }
  }, [savedSpotIds]);

  useEffect(() => {
    try {
      localStorage.setItem('k_spotfinder_visited', JSON.stringify(visitedSpotIds));
    } catch {
      // ignore
    }
  }, [visitedSpotIds]);

  useEffect(() => {
    try {
      localStorage.setItem('k_spotfinder_region', selectedRegion);
    } catch {
      // ignore
    }
  }, [selectedRegion]);

  // Auto-switch to Jeju when user coordinates are on Jeju Island
  useEffect(() => {
    if (userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number') {
      const detected = detectRegionFromCoordinates(userLocation.lat, userLocation.lng);
      if (detected === 'Jeju' && selectedRegion !== 'Jeju') {
        setSelectedRegion('Jeju');
        const toastTxt =
          language === 'kr'
            ? '🍊 제주도 위치 감지! 필터가 제주로 자동 조정되었습니다.'
            : language === 'en'
            ? '🍊 Jeju location detected! Filters adjusted to Jeju.'
            : '🍊 Standort erkannt: Jeju! Filter automatisch auf Jeju angepasst.';
        showToast(toastTxt);
      }
    }
  }, [userLocation.lat, userLocation.lng, selectedRegion, language]);

  useEffect(() => {
    const key = import.meta.env.VITE_KAKAO_MAP_APP_KEY;
    if (key && typeof key === 'string' && key.trim()) {
      loadKakaoMaps(key.trim()).catch((err) => {
        console.warn('Kakao SDK auto load notice:', err);
      });
    }
  }, []);

  // Scroll to top on view mode change (list, map, saved, etc.)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [viewMode]);

  // Auto trigger GPS once on app launch
  useEffect(() => {
    if (navigator.geolocation && window.isSecureContext) {
      handleRequestGps(true);
    }
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleManualLocationSelected = (location: UserLocation) => {
    setIsLocating(false);
    setLocationError(null);
    setLocationNotice(null);
    setUserLocation(location);
    setIsLocationReady(true);
    const detected = detectRegionFromCoordinates(location.lat, location.lng);
    if (detected) {
      setSelectedRegion(detected);
    }
    showToast(`📍 Referenzort gesetzt: ${location.cityName}`);
  };

  // ─── GPS + Reverse Geocoding (One-Shot Query) ───
  const handleRequestGps = (isInitial = false) => {
    if (!navigator.geolocation) {
      if (!isInitial) setLocationError('Dein Browser unterstützt keine Standortermittlung.');
      return;
    }

    if (!window.isSecureContext) {
      if (!isInitial) setLocationError('Standortzugriff erfordert HTTPS oder localhost.');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        const inKorea = isCoordinateInSouthKorea(latitude, longitude);
        if (!inKorea) {
          setLocationNotice(
            language === 'kr'
              ? '한국 외부에 위치하여 기본 도시(서울, 100km 반경) 기준으로 표시합니다.'
              : language === 'en'
              ? 'GPS detected outside South Korea. Standard city Seoul (100 km radius) is active.'
              : 'GPS außerhalb Südkoreas erkannt. Standard-Stadt Seoul (100 km Radius) ist aktiv.'
          );
          setIsLocating(false);
          setLocationError(null);
          setIsLocationReady(true);
          return;
        }

        setLocationNotice(null);

        // Automatic Region Detection
        const detected = detectRegionFromCoordinates(latitude, longitude);
        if (detected) {
          setSelectedRegion(detected);
          if (detected === 'Jeju') {
            const toastTxt =
              language === 'kr'
                ? '🍊 제주도 위치 감지! 필터가 제주로 자동 조정되었습니다.'
                : language === 'en'
                ? '🍊 Jeju location detected! Filters adjusted to Jeju.'
                : '🍊 Standort erkannt: Jeju! Filter automatisch auf Jeju angepasst.';
            showToast(toastTxt);
          }
        }

        const last = lastReverseLocationRef.current;
        const needsReverseGeocode =
          !last || calculateDistance(last.lat, last.lng, latitude, longitude) >= 0.1;

        setIsLocating(false);
        setLocationError(null);
        setIsLocationReady(true);

        setUserLocation((current) => ({
          ...current,
          lat: latitude,
          lng: longitude,
          accuracy,
          heading: position.coords.heading,
          isGps: inKorea,
          cityName: needsReverseGeocode ? 'Standort wird ermittelt…' : current.cityName,
          streetAddressKr: needsReverseGeocode ? undefined : current.streetAddressKr,
          streetAddressEn: needsReverseGeocode ? undefined : current.streetAddressEn,
        }));

        if (!isInitial && detected !== 'Jeju') {
          showToast(
            language === 'kr'
              ? '📍 실시간 GPS 위치가 업데이트되었습니다'
              : language === 'en'
              ? '📍 Live GPS location updated'
              : '📍 Live-GPS aktualisiert'
          );
        }

        if (needsReverseGeocode && inKorea) {
          lastReverseLocationRef.current = { lat: latitude, lng: longitude };
          const reqId = ++reverseRequestIdRef.current;
          try {
            const detailed = await reverseGeocodeCoords(latitude, longitude);
            if (reqId === reverseRequestIdRef.current) {
              const addrKr = detailed.formattedAddressKr || '';
              const addrEn = (detailed.formattedAddressEn || '').toLowerCase();
              const isJejuAddress = addrKr.includes('제주') || addrEn.includes('jeju') || (detailed.cityEn || '').toLowerCase().includes('jeju');
              if (isJejuAddress) {
                setSelectedRegion('Jeju');
              }
              setUserLocation((cur) => ({
                ...cur,
                streetAddressKr: detailed.formattedAddressKr,
                streetAddressEn: detailed.formattedAddressEn,
                cityName: detailed.cityEn || 'Live GPS',
              }));
            }
          } catch {
            // ignore
          }
        }
      },
      (error) => {
        setIsLocating(false);
        if (!isInitial) {
          const failure = getGeolocationFailure(error, language);
          setLocationError(failure.message);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 120000,
      }
    );
  };

  const handleLiveScan = async () => {
    setIsScanningLive(true);
    showToast(
      language === 'kr'
        ? '🛰️ OpenStreetMap을 통해 주변 명소를 스캔 중...'
        : language === 'en'
        ? '🛰️ Scanning nearby places via OpenStreetMap...'
        : '🛰️ Scanne Umgebung über OpenStreetMap...'
    );
    try {
      const liveSpots = await fetchLiveNearbyPois(userLocation.lat, userLocation.lng, 3000);
      if (liveSpots.length > 0) {
        setExtraPois(liveSpots);
        showToast(
          language === 'kr'
            ? `✨ 추가 장소 ${liveSpots.length}곳을 발견했습니다!`
            : language === 'en'
            ? `✨ Found ${liveSpots.length} additional spots!`
            : `✨ ${liveSpots.length} zusätzliche Orte gefunden!`
        );
      } else {
        showToast(
          language === 'kr'
            ? '추가 장소를 찾지 못했습니다.'
            : language === 'en'
            ? 'No additional spots found.'
            : 'Keine weiteren Orte gefunden.'
        );
      }
    } catch {
      showToast(
        language === 'kr'
          ? '스캔 중 오류가 발생했습니다.'
          : language === 'en'
          ? 'Error scanning live places.'
          : 'Fehler beim Scannen.'
      );
    } finally {
      setIsScanningLive(false);
    }
  };

  const liveScanKey = isLocationReady
    ? `${radiusKm}:${userLocation.lat.toFixed(3)}:${userLocation.lng.toFixed(3)}`
    : null;

  useEffect(() => {
    if (!liveScanKey) return;

    let cancelled = false;
    const radiusMeters = radiusKm === 0
      ? 5_000
      : Math.min(5_000, Math.max(500, Math.round(radiusKm * 1000)));

    setIsScanningLive(true);
    const timer = window.setTimeout(async () => {
      try {
        let spotsToAdd: Spot[] = [];

        // 1. Try Kakao Native Places SDK if available
        if (window.kakao?.maps?.services) {
          try {
            const kakaoSpots = await fetchKakaoLivePlaces(window.kakao, userLocation.lat, userLocation.lng, radiusMeters, selectedCategory);
            if (kakaoSpots.length > 0) {
              spotsToAdd = [...spotsToAdd, ...kakaoSpots];
            }
          } catch (e) {
            console.warn('Kakao places query notice:', e);
          }
        }

        // 2. Query OSM / Overpass for extra sights
        const osmSpots = await fetchLiveNearbyPois(userLocation.lat, userLocation.lng, radiusMeters);
        if (osmSpots.length > 0) {
          spotsToAdd = [...spotsToAdd, ...osmSpots];
        }

        if (!cancelled && spotsToAdd.length > 0) {
          setExtraPois((prev) => {
            const existingIds = new Set(prev.map((s) => s.id));
            const fresh = spotsToAdd.filter((s) => !existingIds.has(s.id));
            return [...prev, ...fresh];
          });
        }
      } finally {
        if (!cancelled) setIsScanningLive(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [liveScanKey, radiusKm, userLocation.lat, userLocation.lng]);

  // Toggle Bookmark
  const handleToggleSave = (spot: Spot) => {
    if (savedSpotIds.includes(spot.id)) {
      setSavedSpotIds(savedSpotIds.filter((id) => id !== spot.id));
      showToast(
        language === 'kr'
          ? `저장 해제: ${spot.nameKr}`
          : language === 'en'
          ? `Removed: ${spot.nameKr}`
          : `Entfernt: ${spot.nameKr}`
      );
    } else {
      setSavedSpotIds([...savedSpotIds, spot.id]);
      showToast(
        language === 'kr'
          ? `⭐ 저장됨: ${spot.nameKr}`
          : language === 'en'
          ? `⭐ Saved: ${spot.nameKr}`
          : `⭐ Gemerkt: ${spot.nameKr}`
      );
      confetti({
        particleCount: 30,
        spread: 50,
        origin: { y: 0.8 },
        colors: ['#7c6aef', '#e879a8', '#4ade80', '#60a5fa'],
      });
    }
  };

  // Toggle Visited / Checked off
  const handleToggleVisited = (spot: Spot) => {
    if (visitedSpotIds.includes(spot.id)) {
      setVisitedSpotIds(visitedSpotIds.filter((id) => id !== spot.id));
      showToast(
        language === 'kr'
          ? `미방문으로 변경: ${spot.nameKr}`
          : language === 'en'
          ? `Marked as unvisited: ${spot.nameKr}`
          : `Wieder als unbesucht markiert: ${spot.nameKr}`
      );
    } else {
      setVisitedSpotIds([...visitedSpotIds, spot.id]);
      showToast(
        language === 'kr'
          ? `✓ 방문 완료: ${spot.nameKr}`
          : language === 'en'
          ? `✓ Visited: ${spot.nameKr}`
          : `✓ Abgehakt / Besucht: ${spot.nameKr}`
      );
      confetti({
        particleCount: 35,
        spread: 55,
        origin: { y: 0.8 },
        colors: ['#4ade80', '#34d399', '#6ee7b7', '#059669'],
      });
    }
  };

  const allAvailableSpots = useMemo(() => [...SPOTS_DATA, ...extraPois], [extraPois]);

  // Augmented list with distances.
  // If the user selects a distant region (e.g. Jeju, Busan) while located in Seoul,
  // the strict radius limit is bypassed so all places in that region are immediately visible.
  // When within range (e.g. Seoul or All with radius 100km), the radius filter applies accurately.
  const isSelectedRegionDistant = useMemo(() => {
    if (selectedRegion === 'All') return false;
    const regionSpots = allAvailableSpots.filter((s) => s.region === selectedRegion);
    if (regionSpots.length === 0) return false;
    const minDistance = Math.min(
      ...regionSpots.map((s) => calculateDistance(userLocation.lat, userLocation.lng, s.lat, s.lng))
    );
    return radiusKm > 0 && minDistance > radiusKm;
  }, [allAvailableSpots, selectedRegion, userLocation.lat, userLocation.lng, radiusKm]);

  const effectiveRadius = isSelectedRegionDistant ? 0 : radiusKm;
  const augmentedBaseSpots = useMemo(() => {
    return augmentAndSortSpots(allAvailableSpots, userLocation, effectiveRadius, sortBy);
  }, [allAvailableSpots, userLocation, effectiveRadius, sortBy]);

  // Counts for Smart Moment Bar badges
  const perfectSpotsCount = useMemo(() => {
    return augmentedBaseSpots.filter((s) => isSpotPerfectNow(s, currentMoment)).length;
  }, [augmentedBaseSpots, currentMoment]);

  const walkingSpotsCount = useMemo(() => {
    return augmentedBaseSpots.filter((s) => (s.calculatedDistance ?? 999) <= 0.5).length;
  }, [augmentedBaseSpots]);

  // Region counts consistent with active category filter
  const regionCounts = useMemo(() => {
    const hasSearch = Boolean(searchQuery.trim());
    const counts: Record<string, number> = { All: 0 };
    allAvailableSpots.forEach((s) => {
      if (matchesSpotCategory(s, selectedCategory, s.region, hasSearch)) {
        counts[s.region] = (counts[s.region] || 0) + 1;
        counts.All += 1;
      }
    });
    return counts;
  }, [allAvailableSpots, selectedCategory, searchQuery]);

  // Precompute nearby 500m cluster counts once per spot list update
  const clusterCountsMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const spot of allAvailableSpots) {
      map[spot.id] = findNearbyClusterSpots(spot, allAvailableSpots, 0.5).length;
    }
    return map;
  }, [allAvailableSpots]);

  // Filtered & Sorted spots according to all active criteria
  const filteredAndSortedSpots = useMemo(() => {
    let list = augmentedBaseSpots;
    const hasSearch = Boolean(searchQuery.trim());

    // 0. Region Filter (1-click Seoul / Incheon / Jeju / All)
    if (selectedRegion !== 'All') {
      list = list.filter((s) => s.region === selectedRegion);
    }

    // 1. Smart Moment Filter
    if (smartFilter === 'now_perfect') {
      list = list.filter((s) => isSpotPerfectNow(s, currentMoment));
    } else if (smartFilter === 'minimal_walking') {
      list = list.filter((s) => (s.calculatedDistance ?? 999) <= 0.5);
    } else if (smartFilter === 'indoor') {
      list = list.filter((s) => isIndoorSpot(s));
    }

    // 2. Category Filter (includes cultural_hidden in 'all' and unlocks all categories during active text search)
    list = list.filter((s) =>
      matchesSpotCategory(s, selectedCategory, selectedRegion, hasSearch)
    );

    // 3. Visited Filter
    if (visitedFilter === 'unvisited') {
      list = list.filter((s) => !visitedSpotIds.includes(s.id));
    } else if (visitedFilter === 'visited') {
      list = list.filter((s) => visitedSpotIds.includes(s.id));
    }

    // 4. Search Query
    if (hasSearch) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.nameDe.toLowerCase().includes(q) ||
          s.nameKr.includes(q) ||
          s.district?.toLowerCase().includes(q) ||
          s.region.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q) ||
          s.descriptionDe?.toLowerCase().includes(q) ||
          s.chargingDetailsDe?.toLowerCase().includes(q) ||
          s.chargingDetailsEn?.toLowerCase().includes(q) ||
          (s.chargingDetailsKr && s.chargingDetailsKr.includes(q)) ||
          s.dietDetailsDe?.toLowerCase().includes(q) ||
          s.dietDetailsEn?.toLowerCase().includes(q) ||
          (s.dietType && s.dietType.toLowerCase().includes(q)) ||
          s.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    // 5. Visited / Erledigt spots sink to the bottom when in 'all' view
    if (visitedFilter === 'all') {
      list = [...list].sort((a, b) => {
        const aVisited = visitedSpotIds.includes(a.id);
        const bVisited = visitedSpotIds.includes(b.id);
        if (aVisited !== bVisited) {
          return aVisited ? 1 : -1;
        }
        return 0;
      });
    }

    return list;
  }, [augmentedBaseSpots, selectedRegion, smartFilter, currentMoment, selectedCategory, visitedFilter, searchQuery, visitedSpotIds]);

  const visitedInCurrentViewCount = useMemo(
    () => filteredAndSortedSpots.filter((s) => visitedSpotIds.includes(s.id)).length,
    [filteredAndSortedSpots, visitedSpotIds]
  );

  const isSnackSearch = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length < 2) return false;
    const q = searchQuery.toLowerCase().trim();
    const keywords = [
      'snack',
      'veggie',
      'vegetar',
      'convenience',
      'vegemil',
      'duyu',
      'gs25',
      'cu',
      '7-eleven',
      'eier',
      'kalorien',
      'protein',
      'gewicht',
      '간식',
      '편의점',
    ];
    return keywords.some((k) => q.includes(k));
  }, [searchQuery]);

  const isContentLoading = isLocating;

  const savedSpotsList = useMemo(() => {
    const list = allAvailableSpots.filter((s) => savedSpotIds.includes(s.id));
    const sorted = augmentAndSortSpots(list, userLocation, 0, 'distance');
    return sorted.sort((a, b) => {
      const aVisited = visitedSpotIds.includes(a.id);
      const bVisited = visitedSpotIds.includes(b.id);
      if (aVisited !== bVisited) return aVisited ? 1 : -1;
      return 0;
    });
  }, [allAvailableSpots, savedSpotIds, userLocation, visitedSpotIds]);

  const sortOptions: { value: SortOption; label: string }[] = useMemo(() => [
    {
      value: 'newest',
      label: language === 'kr' ? '✨ 최신 추가순' : language === 'en' ? '✨ Newest First' : '✨ Neueste zuerst',
    },
    {
      value: 'distance',
      label: language === 'kr' ? '📍 가장 가까운 거리' : language === 'en' ? '📍 Shortest Distance' : '📍 Kürzeste Distanz',
    },
    {
      value: 'must_see',
      label: language === 'kr' ? '★ 필수 방문 (Must-See)' : language === 'en' ? '★ Must-See' : '★ Must-See',
    },
    {
      value: 'rating',
      label: language === 'kr' ? '⭐ 최고 평점순' : language === 'en' ? '⭐ Highest Rating' : '⭐ Bewertung',
    },
    {
      value: 'name',
      label: language === 'kr' ? '🔤 이름순 (A-Z)' : language === 'en' ? '🔤 Name (A-Z)' : '🔤 A-Z',
    },
  ], [language]);

  const appTexts = {
    de: {
      allRegions: 'Alle Regionen',
      spotsCount: (n: number) => <span><strong>{n}</strong> Spots</span>,
      filterAll: 'Alle',
      filterUnvisited: 'Offen',
      filterVisited: (n: number) => `Besucht (${n})`,
      radiusNotice: (r: number) => <span>Du siehst Orte im <strong>{r} km</strong> Umkreis. Erweitere für Seoul &amp; ganzes Umland:</span>,
      radius50km: '50 km',
      radiusAllKorea: 'Ganz Korea',
      emptyTitle: 'Keine Spots mit diesem Filter gefunden',
      emptyText: 'Schalte oben auf „Alle Empfehlungen“, erweitere den Suchradius oder wähle einen anderen Ort.',
      resetSmartFilter: 'Smart-Filter zurücksetzen',
      showAllKorea: 'Ganz Korea anzeigen',
      hangulCopiedToast: (nameKr: string) => `한글 kopiert: ${nameKr}`,
      removedToast: 'Entfernt',
      allClearedToast: 'Alle gemerkten Spots gelöscht',
      importedToPlannerToast: (n: number) => `✨ ${n} Stationen in den Routenplaner übernommen!`,
      weekendBannerTitle: 'Wochenend- & Festival-Planer: Freitage, Samstage & Sonntage (Sept–Dez) mit Solo-Tipps!',
      openWeekendPlannerBtn: 'Wochenenden planen',
      navTabs: [
        { id: 'list' as ViewMode | 'weekend', icon: Layers, label: 'Liste' },
        { id: 'map' as ViewMode | 'weekend', icon: MapPin, label: 'Karte' },
        { id: 'saved' as ViewMode | 'weekend', icon: Bookmark, label: 'Gemerkt' },
        { id: 'weekend' as ViewMode | 'weekend', icon: CalendarDays, label: 'Planer' },
      ],
    },
    en: {
      allRegions: 'All Regions',
      spotsCount: (n: number) => <span><strong>{n}</strong> spots</span>,
      filterAll: 'All',
      filterUnvisited: 'Unvisited',
      filterVisited: (n: number) => `Visited (${n})`,
      radiusNotice: (r: number) => <span>Showing spots within <strong>{r} km</strong>. Expand to see all of Seoul &amp; beyond:</span>,
      radius50km: '50 km',
      radiusAllKorea: 'All Korea',
      emptyTitle: 'No spots found with this filter',
      emptyText: 'Switch to "All Recommendations" above, expand the search radius, or select another destination.',
      resetSmartFilter: 'Reset Smart Filter',
      showAllKorea: 'Show All Korea',
      hangulCopiedToast: (nameKr: string) => `Korean name copied: ${nameKr}`,
      removedToast: 'Removed from saved',
      allClearedToast: 'All saved places cleared',
      importedToPlannerToast: (n: number) => `✨ ${n} spots imported into route planner!`,
      weekendBannerTitle: 'Weekend & Festival Planner: Fridays, Saturdays & Sundays (Sep–Dec) with solo tips!',
      openWeekendPlannerBtn: 'Plan Weekends',
      navTabs: [
        { id: 'list' as ViewMode | 'weekend', icon: Layers, label: 'List' },
        { id: 'map' as ViewMode | 'weekend', icon: MapPin, label: 'Map' },
        { id: 'saved' as ViewMode | 'weekend', icon: Bookmark, label: 'Saved' },
        { id: 'weekend' as ViewMode | 'weekend', icon: CalendarDays, label: 'Planner' },
      ],
    },
    kr: {
      allRegions: '전체 지역',
      spotsCount: (n: number) => <span><strong>{n}</strong>개 장소</span>,
      filterAll: '전체',
      filterUnvisited: '미방문',
      filterVisited: (n: number) => `방문 완료 (${n})`,
      radiusNotice: (r: number) => <span>현재 반경 <strong>{r}km</strong> 이내를 표시 중입니다. 서울 및 수도권 전체로 확장:</span>,
      radius50km: '50 km',
      radiusAllKorea: '전국',
      emptyTitle: '선택한 조건의 장소가 없습니다',
      emptyText: '상단의 \'전체 추천\'을 누르거나 검색 반경을 넓혀보세요.',
      resetSmartFilter: '스마트 필터 초기화',
      showAllKorea: '전국 보기',
      hangulCopiedToast: (nameKr: string) => `한글 복사됨: ${nameKr}`,
      removedToast: '저장 목록에서 삭제됨',
      allClearedToast: '모든 저장 장소 삭제됨',
      importedToPlannerToast: (n: number) => `✨ ${n}개 장소가 경로 플래너에 추가되었습니다!`,
      weekendBannerTitle: '주말 & 축제 여행 플래너: 금·토·일 코스 (9~12월) & 혼놀/혼밥 꿀팁!',
      openWeekendPlannerBtn: '주말 플래너 열기',
      navTabs: [
        { id: 'list' as ViewMode | 'weekend', icon: Layers, label: '목록' },
        { id: 'map' as ViewMode | 'weekend', icon: MapPin, label: '지도' },
        { id: 'saved' as ViewMode | 'weekend', icon: Bookmark, label: '저장' },
        { id: 'weekend' as ViewMode | 'weekend', icon: CalendarDays, label: '주말플래너' },
      ],
    },
  }[language];

  return (
    <div className="app-shell">
      {/* Header */}
      <Header
        viewMode={viewMode}
        setViewMode={setViewMode}
        language={language}
        setLanguage={setLanguage}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        savedCount={savedSpotIds.length}
        onOpenCurrencyConverter={() => setIsCurrencyConverterOpen(true)}
        onOpenWeekendPlanner={() => setIsWeekendPlannerOpen(true)}
      />

      {/* Main Content */}
      <main className="container main-content">
        {/* 1-Click Region Switcher (Seoul, Incheon, All) */}
        {(viewMode === 'list' || viewMode === 'map') && (
          <div className="region-pills-container" role="tablist" aria-label="Regionen">
            <div className="region-pills-scroll">
              <button
                onClick={() => setSelectedRegion('All')}
                className={`region-pill ${selectedRegion === 'All' ? 'region-pill--active' : ''}`}
              >
                <span>{appTexts.allRegions}</span>
                <span className="region-pill__badge">{regionCounts['All'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Seoul')}
                className={`region-pill ${selectedRegion === 'Seoul' ? 'region-pill--active' : ''}`}
              >
                <span>Seoul</span>
                <span className="region-pill__badge">{regionCounts['Seoul'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Incheon')}
                className={`region-pill ${selectedRegion === 'Incheon' ? 'region-pill--active' : ''}`}
              >
                <span>Incheon</span>
                <span className="region-pill__badge">{regionCounts['Incheon'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Busan')}
                className={`region-pill ${selectedRegion === 'Busan' ? 'region-pill--active' : ''}`}
              >
                <span>Busan</span>
                <span className="region-pill__badge">{regionCounts['Busan'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Jeju')}
                className={`region-pill ${selectedRegion === 'Jeju' ? 'region-pill--active' : ''}`}
              >
                <span>Jeju</span>
                <span className="region-pill__badge">{regionCounts['Jeju'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Gyeongju')}
                className={`region-pill ${selectedRegion === 'Gyeongju' ? 'region-pill--active' : ''}`}
              >
                <span>Gyeongju</span>
                <span className="region-pill__badge">{regionCounts['Gyeongju'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Gangwon')}
                className={`region-pill ${selectedRegion === 'Gangwon' ? 'region-pill--active' : ''}`}
              >
                <span>Gangwon</span>
                <span className="region-pill__badge">{regionCounts['Gangwon'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Jeonju')}
                className={`region-pill ${selectedRegion === 'Jeonju' ? 'region-pill--active' : ''}`}
              >
                <span>Jeonju</span>
                <span className="region-pill__badge">{regionCounts['Jeonju'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Daegu')}
                className={`region-pill ${selectedRegion === 'Daegu' ? 'region-pill--active' : ''}`}
              >
                <span>Daegu</span>
                <span className="region-pill__badge">{regionCounts['Daegu'] ?? 0}</span>
              </button>
              <button
                onClick={() => setSelectedRegion('Gyeonggi')}
                className={`region-pill ${selectedRegion === 'Gyeonggi' ? 'region-pill--active' : ''}`}
              >
                <span>Gyeonggi</span>
                <span className="region-pill__badge">{regionCounts['Gyeonggi'] ?? 0}</span>
              </button>
            </div>
          </div>
        )}

        {/* Location Bar (List & Map Views) */}
        {(viewMode === 'list' || viewMode === 'map') && (
          <LocationSelector
            userLocation={userLocation}
            radiusKm={radiusKm}
            setRadiusKm={setRadiusKm}
            onRequestGps={handleRequestGps}
            isLocating={isLocating}
            locationError={locationError}
            locationNotice={locationNotice}
            onManualLocationSelected={handleManualLocationSelected}
            onLiveScan={handleLiveScan}
            isScanningLive={isScanningLive}
            language={language}
          />
        )}

        {/* Category Filter */}
        {(viewMode === 'list' || viewMode === 'map') && (
          <CategoryFilter
            selectedCategory={selectedCategory}
            onSelectCategory={handleSelectCategory}
            language={language}
          />
        )}

        {/* Smart Moment Bar (Only in List View) */}
        {viewMode === 'list' && (
          <SmartMomentBar
            currentMoment={currentMoment}
            customHour={customHour}
            onSelectCustomHour={setCustomHour}
            smartFilter={smartFilter}
            onSelectSmartFilter={setSmartFilter}
            language={language}
            perfectSpotsCount={perfectSpotsCount}
            walkingSpotsCount={walkingSpotsCount}
          />
        )}

        {/* ─── List View ─── */}
        {viewMode === 'list' && (
          <>
            {/* 13 Cool Daytrip Extras Banner (When Extras category is active) */}
            {selectedCategory === 'extras' && (
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.14), rgba(139, 92, 246, 0.14))',
                  border: '1px solid rgba(244, 63, 94, 0.3)',
                  borderRadius: 16,
                  padding: '16px 20px',
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #f43f5e, #8b5cf6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      flexShrink: 0,
                      boxShadow: '0 0 12px rgba(244, 63, 94, 0.45)',
                    }}
                  >
                    <Flame style={{ width: 22, height: 22 }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.01em' }}>
                      {language === 'kr'
                        ? '서울/인천 근교 1~2시간 이색 명소 13선'
                        : language === 'en'
                        ? '13 Cool Escapes (1–2h from Incheon / Seoul)'
                        : '13 Coole Ausflugs-Extras (1–2h ab Incheon / Seoul)'}
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8', lineHeight: 1.4 }}>
                      {language === 'kr'
                        ? '지하광산·동굴, 교동도 대룡시장(북한 2.5km), 갯벌 ATV, 해상케이블카, 바사트협곡 & 사극오픈세트'
                        : language === 'en'
                        ? 'Underground caves, North Korea border market, mudflat ATVs, marine cable cars, basalt canyons & historic film sets'
                        : 'Lichtkunst-Höhlen, 1960er Nordkorea-Grenzinsel, Watt-Quads, Meeres-Seilbahn, Vulkan-Schlucht & Historien-Filmsets'}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    padding: '6px 12px',
                    borderRadius: 20,
                    background: 'rgba(244, 63, 94, 0.2)',
                    color: '#fb7185',
                    border: '1px solid rgba(244, 63, 94, 0.35)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  13 Spots
                </div>
              </div>
            )}

            {/* 24/7 Convenience Store Veggie Snacks Banner (When Vegetarian category is active) */}
            {selectedCategory === 'vegetarian_food' && (
              <div
                className="veggie-snack-banner"
                onClick={() => setIsVeggieSnacksOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setIsVeggieSnacksOpen(true);
                }}
              >
                <div className="veggie-snack-banner__left">
                  <div className="veggie-snack-banner__icon">
                    <Store style={{ width: 18, height: 18 }} />
                  </div>
                  <div className="veggie-snack-banner__text">
                    <div className="veggie-snack-banner__title-row">
                      <span className="veggie-snack-banner__title">
                        {language === 'kr'
                          ? '24/7 편의점 채식 간식 가이드 (CU · GS25 · 7-Eleven)'
                          : language === 'en'
                          ? '24/7 Convenience Store Veggie Snacks (CU · GS25 · 7-Eleven)'
                          : '24/7 Convenience Store Veggie-Snacks (CU · GS25 · 7-Eleven)'}
                      </span>
                      <span className="veggie-snack-banner__tag">
                        {language === 'kr'
                          ? '⚡ 칼로리 & 단백질'
                          : language === 'en'
                          ? '⚡ Calories & Protein'
                          : '⚡ Kalorien & Protein'}
                      </span>
                    </div>
                    <p className="veggie-snack-banner__sub">
                      {language === 'kr'
                        ? '구운계란, 하루견과, 베지밀 B, 두부바 – 체중 감소를 막아주는 24시간 필수 간식'
                        : language === 'en'
                        ? 'Roasted eggs, mixed nuts, Vegemil B, tofu bars – quick fuel to combat rapid weight loss'
                        : 'Geröstete Eier, Nussmix, Vegemil B & Tofu-Riegel – Sofortige Kalorien & Protein gegen Gewichtsverlust'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn--sm btn--primary veggie-snack-banner__btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsVeggieSnacksOpen(true);
                  }}
                >
                  <span>
                    {language === 'kr'
                      ? '가이드 열기'
                      : language === 'en'
                      ? 'Open Guide'
                      : 'Guide öffnen'}
                  </span>
                </button>
              </div>
            )}

            {/* Snack Search Helper Hint */}
            {isSnackSearch && selectedCategory !== 'vegetarian_food' && (
              <div className="veggie-search-hint">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Store style={{ width: 14, height: 14, color: '#34d399', flexShrink: 0 }} />
                  <span>
                    {language === 'kr'
                      ? '💡 편의점 채식 간식 및 칼로리 보충 정보가 필요하신가요?'
                      : language === 'en'
                      ? '💡 Looking for 24/7 convenience store veggie snacks & calories?'
                      : '💡 Suchst du nach 24/7 Convenience Store Veggie-Snacks & Kalorien?'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsVeggieSnacksOpen(true)}
                  className="btn btn--sm btn--primary"
                  style={{ padding: '3px 10px', fontSize: 11, flexShrink: 0 }}
                >
                  {language === 'kr'
                    ? '간식 가이드 열기'
                    : language === 'en'
                    ? 'Open Guide'
                    : 'Guide öffnen'}
                </button>
              </div>
            )}

            {/* Filter Bar */}
            <div className="filter-bar">
              
              {/* Left: count + visited filter */}
              <div className="filter-bar__left">
                <div className="filter-bar__count">
                  {appTexts.spotsCount(filteredAndSortedSpots.length)}
                </div>

                {/* Visited toggle */}
                <div className="toggle-group">
                  <button
                    onClick={() => setVisitedFilter('all')}
                    className={`toggle-btn ${visitedFilter === 'all' ? 'toggle-btn--active' : ''}`}
                  >
                    {appTexts.filterAll}
                  </button>
                  <button
                    onClick={() => setVisitedFilter('unvisited')}
                    className={`toggle-btn toggle-btn--accent ${visitedFilter === 'unvisited' ? 'toggle-btn--active' : ''}`}
                  >
                    {appTexts.filterUnvisited}
                  </button>
                  <button
                    onClick={() => setVisitedFilter('visited')}
                    className={`toggle-btn toggle-btn--success ${visitedFilter === 'visited' ? 'toggle-btn--active' : ''}`}
                  >
                    <CheckCircle2 style={{ width: 12, height: 12 }} />
                    <span>{appTexts.filterVisited(visitedSpotIds.length)}</span>
                  </button>
                </div>
              </div>

              {/* Sort dropdown */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="sort-select"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Smart Radius Expand Banner */}
            {radiusKm > 0 && radiusKm <= 15 && (
              <div className="alert-box flex items-center justify-between" style={{ padding: '10px 14px', background: 'rgba(124, 106, 239, 0.08)', borderColor: 'rgba(124, 106, 239, 0.25)' }}>
                <div className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <Sparkles style={{ width: 15, height: 15, color: 'var(--accent-text)', flexShrink: 0 }} />
                  <span>
                    {appTexts.radiusNotice(radiusKm)}
                  </span>
                </div>
                <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                  <button onClick={() => setRadiusKm(50)} className="btn btn--primary btn--sm" style={{ padding: '3px 10px', fontSize: 11 }}>
                    {appTexts.radius50km}
                  </button>
                  <button onClick={() => setRadiusKm(0)} className="btn btn--ghost btn--sm" style={{ padding: '3px 10px', fontSize: 11 }}>
                    {appTexts.radiusAllKorea}
                  </button>
                </div>
              </div>
            )}

            {/* Grid */}
            {isContentLoading ? (
              <div className="spot-grid">
                {Array.from({ length: 8 }, (_, index) => (
                  <div key={index} className="skeleton" />
                ))}
              </div>
            ) : filteredAndSortedSpots.length === 0 ? (
              <div className="empty-state">
                <SearchX className="empty-state__icon" style={{ width: 40, height: 40 }} />
                <h3 className="empty-state__title">{appTexts.emptyTitle}</h3>
                <p className="empty-state__text">
                  {appTexts.emptyText}
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                  {smartFilter !== 'all' && (
                    <button onClick={() => setSmartFilter('all')} className="btn btn--primary btn--sm">
                      {appTexts.resetSmartFilter}
                    </button>
                  )}
                  <button onClick={() => setRadiusKm(0)} className="btn btn--ghost btn--sm">
                    {appTexts.showAllKorea}
                  </button>
                </div>
              </div>
            ) : (
              <div className="spot-grid">
                {filteredAndSortedSpots.map((spot, index) => {
                  const isVisited = visitedSpotIds.includes(spot.id);
                  const isFirstVisited =
                    visitedFilter === 'all' &&
                    isVisited &&
                    index > 0 &&
                    !visitedSpotIds.includes(filteredAndSortedSpots[index - 1].id);
                  const clusterCount = clusterCountsMap[spot.id] ?? 0;

                  return (
                    <React.Fragment key={spot.id}>
                      {isFirstVisited && (
                        <div className="visited-divider-banner">
                          <div className="visited-divider-banner__line" />
                          <div className="visited-divider-banner__pill">
                            <CheckCircle2 style={{ width: 14, height: 14, color: 'var(--success)' }} />
                            <span>
                              {language === 'kr'
                                ? `방문 완료한 장소 (${visitedInCurrentViewCount})`
                                : language === 'en'
                                ? `Already Visited & Done (${visitedInCurrentViewCount})`
                                : `Bereits erledigt & abgehakt (${visitedInCurrentViewCount})`}
                            </span>
                          </div>
                          <div className="visited-divider-banner__line" />
                        </div>
                      )}
                      <SpotCard
                        spot={spot}
                        userLocation={userLocation}
                        language={language}
                        isSaved={savedSpotIds.includes(spot.id)}
                        isVisited={isVisited}
                        currentMoment={currentMoment}
                        nearbyClusterCount={clusterCount}
                        onToggleSave={handleToggleSave}
                        onToggleVisited={handleToggleVisited}
                        onCopyHangul={(nameKr) => showToast(appTexts.hangulCopiedToast(nameKr))}
                        onSelectSpot={(s) => setSelectedSpotDetail(s)}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ─── Map View ─── */}
        {viewMode === 'map' && (
          <MapView
            spots={filteredAndSortedSpots}
            userLocation={userLocation}
            radiusKm={radiusKm}
            onSelectSpot={(s) => setSelectedSpotDetail(s)}
            language={language}
            onRequestGps={handleRequestGps}
            isLocationReady={isLocationReady}
          />
        )}

        {/* ─── Saved View ─── */}
        {viewMode === 'saved' && (
          <FavoritesDrawer
            savedSpots={savedSpotsList}
            language={language}
            userLocation={userLocation}
            onRemoveSaved={(id) => {
              setSavedSpotIds(savedSpotIds.filter((sid) => sid !== id));
              showToast(appTexts.removedToast);
            }}
            onClearAll={() => {
              setSavedSpotIds([]);
              showToast(appTexts.allClearedToast);
            }}
            onSelectSpot={(s) => setSelectedSpotDetail(s)}
            onOpenRoutePlanner={() => setViewMode('route_planner')}
          />
        )}

        {/* ─── Route Planner View ─── */}
        {viewMode === 'route_planner' && (
          <RoutePlannerModal
            savedSpots={savedSpotsList}
            allSpots={allAvailableSpots}
            userLocation={userLocation}
            language={language}
            onClose={() => setViewMode('list')}
            onRemoveSaved={(id) => setSavedSpotIds(savedSpotIds.filter((sid) => sid !== id))}
            onSelectSpot={(s) => setSelectedSpotDetail(s)}
          />
        )}

        {/* ─── Privacy & Test Environment Footer ─── */}
        <PrivacyFooter
          language={language}
          onOpenVeggieSnacks={() => setIsVeggieSnacksOpen(true)}
        />

      </main>

      {/* Spot Detail Modal */}
      <SpotDetailModal
        spot={selectedSpotDetail}
        allSpots={allAvailableSpots}
        onClose={() => setSelectedSpotDetail(null)}
        language={language}
        userLocation={userLocation}
        isSaved={selectedSpotDetail ? savedSpotIds.includes(selectedSpotDetail.id) : false}
        onToggleSave={handleToggleSave}
        onCopyHangul={(nameKr) => showToast(appTexts.hangulCopiedToast(nameKr))}
        onSelectSpot={(s) => setSelectedSpotDetail(s)}
        onOpenVeggieSnacks={() => setIsVeggieSnacksOpen(true)}
      />

      {/* 24/7 Convenience Store Veggie Snacks Modal */}
      <VeggieSnacksModal
        isOpen={isVeggieSnacksOpen}
        onClose={() => setIsVeggieSnacksOpen(false)}
        language={language}
        onCopyToast={(msg) => showToast(msg)}
      />

      {/* Currency Converter Modal */}
      <CurrencyConverterModal
        isOpen={isCurrencyConverterOpen}
        onClose={() => setIsCurrencyConverterOpen(false)}
        language={language}
      />

      {/* Weekend & Festival Planner Modal */}
      {isWeekendPlannerOpen && (
        <WeekendPlannerModal
          isOpen={isWeekendPlannerOpen}
          onClose={() => setIsWeekendPlannerOpen(false)}
          language={language}
          userLocation={userLocation}
          savedSpotIds={savedSpotIds}
          onImportSpotsToPlanner={(spotIds) => {
            const merged = Array.from(new Set([...savedSpotIds, ...spotIds]));
            setSavedSpotIds(merged);
            showToast(appTexts.importedToPlannerToast(spotIds.length));
            setViewMode('route_planner');
          }}
          onSelectSpot={(spot) => setSelectedSpotDetail(spot)}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="toast">
          <Sparkles style={{ width: 16, height: 16, color: 'var(--accent-text)', flexShrink: 0 }} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav" aria-label="Mobile Navigation">
        {appTexts.navTabs.map((tab) => {
          const Icon = tab.icon;
          const isWeekend = tab.id === 'weekend';
          const isActive = isWeekend ? isWeekendPlannerOpen : viewMode === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                if (isWeekend) {
                  setIsWeekendPlannerOpen(true);
                } else {
                  setViewMode(tab.id as ViewMode);
                }
              }}
              className={`bottom-nav__tab ${isActive ? 'bottom-nav__tab--active' : ''} ${isWeekend ? 'bottom-nav__tab--weekend' : ''}`}
            >
              <Icon style={{ width: 20, height: 20 }} />
              <span>{tab.label}</span>
              {tab.id === 'saved' && savedSpotIds.length > 0 && (
                <span className="bottom-nav__badge">{savedSpotIds.length}</span>
              )}
              {isWeekend && (
                <span className="bottom-nav__weekend-dot" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default App;
